import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import ToYouMatchBranchesButton from '../ToYouBranches/ToYouMatchBranchesButton';
import { IT360IntegrationSettings } from '~/types/integrations';
import Switcher from '~/view/components/Controls/Switcher/Switcer';
import t360IntegrationStore from '~/view/screens/SettingsScreen/store/t360IntegrationStore';
import Text, { TEXT } from '~/view/components/Text/Text';
import { localeStore } from '~/mobx/localesStore';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import { TBooleanInNumber } from '~/types/main';
import T360Timeslots from '~/view/screens/SettingsScreen/screens/Integrations/Integrations/components/T360Timeslots/T360Timeslots';
import T360Settings from '~/view/screens/SettingsScreen/screens/Integrations/Integrations/components/T360Settings/T360Settings';
import T360LastSyncInfo from '~/view/screens/SettingsScreen/screens/Integrations/Integrations/components/T360LastSyncInfo/T360LastSyncInfo';
import { AGGREGATORS } from '~/constants/aggregators';
import './style.scss';
import T360BranchesMapping from '~/view/screens/SettingsScreen/screens/Integrations/Integrations/components/T360BranchesMapping/T360BranchesMapping';

interface IProps {
  integrationSettings: IT360IntegrationSettings;
}

const T360IntegrationSettingsForm: React.FC<IProps> = observer((props: IProps) => {
  const integrationSettingsTemp = useMemo(
    () => t360IntegrationStore.integrationsSettingsTemp[props.integrationSettings.id],
    [t360IntegrationStore.integrationsSettingsTemp]
  );

  if (!integrationSettingsTemp) {
    return null;
  }

  const enableIntegration = (status: TBooleanInNumber) => {
    const settings = JSON.parse(JSON.stringify(integrationSettingsTemp));
    settings.enabled = status;
    t360IntegrationStore.updateIntegrationSettingsTemp(props.integrationSettings.id, settings);
  };

  const sendSettings = () => {
    t360IntegrationStore.updateSettings(props.integrationSettings.id);
  };

  const runSync = () => {
    t360IntegrationStore.runSync(props.integrationSettings.id);
  };

  const authorize = () => {
    if (!props.integrationSettings.settings.authorization_url) {
      return;
    }

    window.location.href = 'https://' + String(props.integrationSettings.settings.authorization_url);
  };

  return (
    <div className="t360-integration-settings-form mt-3" key={`t360-integration-${props.integrationSettings.name}-settings`}>
      <div className="d-flex flex-row align-items-center mt-4">
        <Switcher
          className="mr-4"
          status={integrationSettingsTemp.enabled}
          onChange={enableIntegration}
          disabled={t360IntegrationStore.isOnLoading || props.integrationSettings.is_active_sync === 1}
        />
        <Text
          className="mr-3 foodics-authorized-form__checkbox-title"
          type={TEXT.CUSTOM}
          text={`${localeStore.t('enable')} ${localeStore.t(props.integrationSettings.provider)}`}
          disabled={t360IntegrationStore.isOnLoading || props.integrationSettings.is_active_sync === 1}
        />
      </div>
      <div>
        <T360Settings
          integrationId={props.integrationSettings.id}
          disabled={t360IntegrationStore.isOnLoading || props.integrationSettings.is_active_sync === 1}
        />
        {integrationSettingsTemp.settings.hasOwnProperty('timeslots') ? (
          <T360Timeslots
            integrationId={props.integrationSettings.id}
            disabled={t360IntegrationStore.isOnLoading || props.integrationSettings.is_active_sync === 1}
          />
        ) : null}
        {props.integrationSettings.settings.authorization_url && props.integrationSettings.is_empty_tokens ? (
          <Button className="mb-4" onClick={authorize}>
            {localeStore.t('t2_foodics_auth')}
          </Button>
        ) : null}
        {integrationSettingsTemp.settings.hasOwnProperty('branches_mapping') ? (
          <T360BranchesMapping
            integrationId={props.integrationSettings.id}
            disabled={t360IntegrationStore.isOnLoading || props.integrationSettings.is_active_sync === 1}
          />
        ) : null}
        <ToYouMatchBranchesButton integrationId={integrationSettingsTemp.id} disabled={t360IntegrationStore.isValidCredentials(AGGREGATORS.TO_YOU)} />
        <T360LastSyncInfo
          last_sync_successful={props.integrationSettings.last_sync_successful}
          last_sync_date={props.integrationSettings.last_sync_date}
          is_active_sync={props.integrationSettings.is_active_sync}
          last_sync_error={props.integrationSettings.last_sync_error}
        />
        <div className="d-flex flex-row align-items-center justify-content-start mt-8">
          <Button
            className="mr-2"
            onClick={sendSettings}
            disabled={t360IntegrationStore.isOnLoading || props.integrationSettings.is_active_sync === 1}
          >
            {localeStore.t('save')}
          </Button>
          <Button onClick={runSync} disabled={t360IntegrationStore.isOnLoading || props.integrationSettings.is_active_sync === 1}>
            {localeStore.t('Sync')}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default T360IntegrationSettingsForm;
